import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'de8bcu0p2';

// Default images (used when Cloudinary has no images)
const DEFAULT_IMAGES = [
  { src: 'https://customer-assets.emergentagent.com/job_78008f49-3917-4411-8957-ba588b83fbcc/artifacts/o2yhsu3g_image%2001.jfif', title: 'School Entrance', category: 'Campus' },
  { src: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/opd0l82s_secred%20new%2001.jfif', title: 'Lamp Lighting Ceremony', category: 'Events' },
  { src: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/aosrije0_65a756c4-8e2d-4ad0-b450-c78855e60bc7.jfif', title: 'Indoor Play Area', category: 'Facilities' },
  { src: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/0n053mk5_secred%20new%202.jfif', title: 'Rainbow Teacher', category: 'Activities' },
  { src: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/3n7q0iw0_secred%2003.jfif', title: 'Award Ceremony', category: 'Events' },
  { src: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/gdsm9xb4_f55c8095-49f2-4138-8cae-1ab16a988922.jfif', title: 'Achievement Awards', category: 'Events' },
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [cloudinaryImages, setCloudinaryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  // Fetch images from Cloudinary using the Resource List API
  // IMPORTANT: This requires images to be uploaded with tag 'sacred_heart_gallery'
  // AND "Resource List" must be enabled in Cloudinary Settings > Security
  useEffect(() => {
    const fetchCloudinaryImages = async () => {
      try {
        // Cloudinary Resource List API uses TAGS, not folders
        // URL format: https://res.cloudinary.com/{cloud_name}/image/list/{tag}.json
        const response = await fetch(
          `https://res.cloudinary.com/${CLOUD_NAME}/image/list/sacred_heart_gallery.json`
        );
        
        console.log('Cloudinary fetch status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Cloudinary response:', data);
          
          if (data.resources && data.resources.length > 0) {
            const images = data.resources.map((resource, index) => ({
              src: `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/v${resource.version}/${resource.public_id}.${resource.format}`,
              title: `Gallery Image ${index + 1}`,
              category: 'Gallery',
            }));
            setCloudinaryImages(images);
            console.log('Loaded', images.length, 'images from Cloudinary');
          }
        } else {
          console.log('Cloudinary list returned status:', response.status);
        }
      } catch (error) {
        console.log('Cloudinary fetch error:', error.message);
      }
      setLoading(false);
    };

    fetchCloudinaryImages();
  }, []);

  // Combine default and cloudinary images
  const allImages = [...cloudinaryImages, ...DEFAULT_IMAGES];

  const categories = ['All', ...new Set(allImages.map(img => img.category))];

  const filteredImages = activeCategory === 'All' 
    ? allImages 
    : allImages.filter(img => img.category === activeCategory);

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-pink-50 via-purple-50 to-cyan-50" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center">
            <span className="font-accent text-xl text-pink-500 inline-block -rotate-2 mb-2">
              Our Gallery
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-800 tracking-wider mb-6">
              MOMENTS OF JOY
            </h1>
            <p className="font-body text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Take a glimpse into the vibrant life at Sacred Heart School. From classroom activities to special celebrations, every moment is a memory.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap justify-center gap-3"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2.5 rounded-full font-body font-semibold text-sm transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg shadow-pink-500/30'
                    : 'bg-white text-slate-600 hover:bg-purple-50 border border-purple-100'
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-10 h-10 text-pink-500 animate-spin" />
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              <AnimatePresence mode="popLayout">
                {filteredImages.map((image, index) => (
                  <motion.div
                    key={image.src}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className={`relative group cursor-pointer ${
                      index === 0 ? 'sm:col-span-2 sm:row-span-2' : ''
                    }`}
                    onClick={() => setSelectedImage(image)}
                    data-testid={`gallery-image-${index}`}
                  >
                    <div className={`rounded-[2rem] overflow-hidden shadow-lg border-4 border-white ${
                      index === 0 ? 'h-[400px] sm:h-[500px]' : 'h-[250px] sm:h-[300px]'
                    }`}>
                      <img
                        src={image.src}
                        alt={image.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-purple-900/70 via-purple-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-[2rem]">
                        <div className="absolute bottom-6 left-6 right-6">
                          <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-body font-semibold mb-2">
                            {image.category}
                          </span>
                          <h3 className="font-heading text-xl font-semibold text-white tracking-wider">
                            {image.title.toUpperCase()}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}

          {/* More photos coming */}
          <motion.div {...fadeInUp} className="text-center mt-16">
            <div className="bg-white p-12 rounded-[2rem] shadow-lg border border-purple-100">
              <h3 className="font-heading text-2xl font-semibold text-slate-800 tracking-wider mb-4">
                MORE PHOTOS COMING SOON!
              </h3>
              <p className="font-body text-slate-600 max-w-md mx-auto">
                We're constantly updating our gallery with new moments. Check back soon to see more of our wonderful school activities!
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox"
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              data-testid="close-lightbox"
              aria-label="Close lightbox"
            >
              <X className="w-6 h-6" />
            </button>
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-5xl max-h-[90vh] rounded-[2rem] overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.title}
                className="w-full h-full object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-body font-semibold mb-2">
                  {selectedImage.category}
                </span>
                <h3 className="font-heading text-2xl font-semibold text-white tracking-wider">
                  {selectedImage.title.toUpperCase()}
                </h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
